import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useLanguage } from "../../../stores/LanguageStore";
import ColumnEditor from "./ColumnEditor";
import { useState } from "react";
import TagsRessourceSelector from "../../../components/Selectors/TagsRessourceSelector";
import { getTagBackgroundColor } from "../../../functions/utilFunctions";
import { IconX } from "@tabler/icons-react";
import TagsColorEditor from "./tagsColorEditor";
import { useConfigStore } from "../../../stores/ConfigStore";
import { createTag } from "../../../api/TagsAPI";

const TagEditor = ({
  field,
  editFunction,
  cell,
  closePopover,
  apiRef,
  onEdited,
}) => {
  const lang = useLanguage((s) => s.language);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState(cell?.row[field]);

  const isMobile = window.innerWidth < 768;

  const handleSubmit = async (e) => {
    e.preventDefault();

    let fd = new FormData(e.target);
    let data = Object.fromEntries(fd);

    // let tagColor = document.getElementById("new-tag-color")?.value

    // if (tagColor) {
    //   await createTag({
    //   name: search,
    //   color: color ? color.substring(1) : undefined,
    // })
    // }

    setLoading(true);
    editFunction({
      id: cell.row.id,
      tags_ids: tags?.map((x) => x.id),
    })
      .then((response) => {
        closePopover();
        apiRef?.current?.updateRows([{ id: cell.row.id, [field]: tags }]);
        onEdited?.(tags);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Stack component={"form"} onSubmit={handleSubmit} gap={"xs"} w={275}>
      <TagsRessourceSelector
        value={tags}
        setValue={setTags}
        autoFocus={isMobile ? false : true}
        isMobile={isMobile}
      />
      <Group wrap="nowrap" justify="end">
        <Button
          onClick={closePopover}
          size="compact-sm"
          variant="subtle"
          disabled={loading}
        >
          {lang.components.closer.submit}
        </Button>
        <Button
          type="submit"
          loading={loading}
          size="compact-sm"
          color="tertiary.8"
        >
          {lang.contact_lists.edit.dialog.submit_button}
        </Button>
      </Group>
    </Stack>
  );
};

const TagsCell = ({
  cell,
  apiRef,
  editFunction,
  wrapperProps,
  onEdited,
  handleFilter,
  field = "tags",
}) => {
  const lang = useLanguage((s) => s.language);
  useConfigStore((s) => s.tags);
  const tags = cell?.row[field];
  
  const isMobile = window.innerWidth < 768;
  const mainEdit =
    typeof editFunction === "function" ? editFunction(cell.row) : editFunction;

  const handleDelete = (tag) => {
    const newTags = cell?.row[field]?.filter((x) => x.name !== tag);
    apiRef?.current?.updateRows([{ id: cell.row.id, [field]: newTags }]);

    onEdited?.(newTags);

    mainEdit({
      id: cell.row.id,
      tags_ids: newTags.map((x) => x.id),
    });
  };

  return (
    <Group
      wrap="nowrap"
      w={"100%"}
      bg={"gray.1"}
      p={4}
      style={{ borderRadius: 8, overflow: "hidden" }}
      pos={"relative"}
      {...wrapperProps}
    >
      {cell.value ? (
        <ColumnEditor
          disabled={
            !cell.row?.write_permission &&
            !cell?.row?.permission?.write_permission
          }
          isAdd={cell.row?.tags?.length === 0}
          dropDownComponent={(closePopover) => (
            <TagEditor
              editFunction={mainEdit}
              cell={cell}
              closePopover={closePopover}
              apiRef={apiRef}
              onEdited={onEdited}
              field={field}
            />
          )}
          customOpener={(handleOpen, ref) => (
            <Group
              ref={ref}
              flex={"1 1 auto"}
              wrap="nowrap"
              gap={"xs"}
              onClick={handleOpen}
            >
              {tags.map((tag) => (
                <Tooltip
                  label={`${handleFilter ? lang.tags.filter : ""} ${tag.name}`}
                  key={tag.id}
                  color="primary"
                >
                  <Badge
                    variant="default"
                    style={{ cursor: "pointer", border: "none" }}
                    onClick={(e) => {
                      if (!isMobile) {
                        e.stopPropagation();
                      }
                      if (handleFilter) {
                        return handleFilter(tag.name);
                      } else {
                        apiRef?.current?.setFilterModel({
                          items: [
                            {
                              field,
                              operator: "contains",
                              value: tag.name,
                            },
                          ],
                          logicOperator: "and",
                          quickFilterLogicOperator: "and",
                          quickFilterValues: [],
                        });
                      }
                    }}
                    size="md"
                    radius={"md"}
                    fz={12}
                    bg={getTagBackgroundColor(tag.name)}
                    fw={500}
                    pos={"relative"}
                    className="tag-item"
                    tt={"none"}
                  >
                    {!isMobile && <TagsColorEditor tag={tag} />}

                    {tag.name}

                    {!isMobile && (
                      <ActionIcon
                        className="deleter"
                        size={"xs"}
                        pos={"absolute"}
                        top={3}
                        right={4}
                        radius={"sm"}
                        onClick={() => handleDelete(tag.name)}
                        color="red"
                      >
                        <IconX />
                      </ActionIcon>
                    )}
                  </Badge>
                </Tooltip>
              ))}
            </Group>
          )}
        />
      ) : (
        <ColumnEditor
          disabled={
            !cell.row?.write_permission &&
            !cell?.row?.permission?.write_permission
          }
          dropDownComponent={(closePopover) => (
            <TagEditor
              editFunction={mainEdit}
              cell={cell}
              closePopover={closePopover}
              apiRef={apiRef}
              onEdited={onEdited}
              field={field}
            />
          )}
          customOpener={(handleOpen, ref) => (
            <Group ref={ref} flex={"1 1 auto"} onClick={handleOpen} p={4}>
              <Text fz={12} opacity={0.3} fw={400} truncate>
                {lang.tags.browser_tab_title}
              </Text>
            </Group>
          )}
        />
      )}
    </Group>
  );
};

export default TagsCell;
